<template>

  <div :class="showBackground ? 'edit-pane' : ''"
       @pointerdown="onFormInteract"
       @keydown="onFormInteract"
       @focusin="onFormInteract">
      <el-row>
        <el-col :xs="24" :sm="24" :md="20" :lg="20">
          <span v-if="deletable  && !adding">
            <el-button :type="deleteButtonType" size="mini" @click="deleted"
             icon="fa fa-trash-alt"
             :round="deleteButtonType === 'text' ? false : round"
             :disabled="this.isViewOnly()">
              <span v-if="showButtonText" class="hidden-md-and-down"> Delete</span>
            </el-button>&nbsp;&nbsp;
          </span>
          <span v-if="title">
            <span v-if="!titleHeading"><span v-if="prefix" class="text-info">{{prefix}}:&nbsp;</span><span class="text-primary" v-safe-html="title"></span></span>
            <span :is="titleHeading" v-else><span v-if="prefix">{{prefix}}:&nbsp;</span>{{title}}</span>
          </span>
          <span v-else>
            &nbsp;
          </span>
          <!-- XOL-67 (B4): optional, after the title: the option layer's status tag. Nothing renders when unused. -->
          <slot name="title-extra" />
        </el-col>

        <el-col v-if="adding || showEditButton" :class="isMediumOrLess ? 'text-center'  : 'text-right'" :span="4" >

          <div v-if="isEditMode && (this.canChange() || selfEditable)">
            <el-button type="warning" size="mini" @click="cancel" icon="fa fa-times-circle" :round="round"><span v-if="showButtonText" class="hidden-md-and-down"> Cancel</span></el-button>&nbsp;
            <el-button type="success" size="mini" @click="save" icon="fa fa-check" :round="round" :disabled="saveDisabled" :title="saveDisabled ? saveDisabledTitle : null"><span v-if="showButtonText" class="hidden-md-and-down">Save</span></el-button>
          </div>

          <div v-else>
            <span v-if="editable && !adding">
              <el-button :type="editButtonType"
                size="mini" @click="edit"
                icon="fa fa-edit"
                :round="editButtonType === 'text' ? false : round"
                :disabled="this.isViewOnly() && !selfEditable">
                <span v-if="showButtonText" class="hidden-md-and-down">  Edit</span>
              </el-button>


            </span>
            <span v-if="adding">
              <el-button :type="addButtonType" size="mini" @click="add" icon="fa fa-plus-circle" :round="addButtonType === 'text' ? false : round" :disabled="saveDisabled" :title="saveDisabled ? saveDisabledTitle : null">
                <span v-if="showButtonText" class="hidden-md-and-down">  Add</span>
              </el-button>
            </span>
          </div>

          <br />
        </el-col>

      </el-row>

      <el-row align="start">
        <el-col :span="24">
          <slot />
        </el-col>
      </el-row>

    </div>
</template>

<script>
import DrawerDirtyMixin from '@/mixins/DrawerDirtyMixin'

export default {
  name: 'edit-pane',

  mixins: [DrawerDirtyMixin],

  props: {
    prefix: {
      type: String,
      default: null
    },

    title: {
      type: String,
      default: ''
    },

    showTitle: {
      type: Boolean,
      default: true
    },

    titleHeading: {
      type: String,
      default: null
    },

    editable: {
      type: Boolean,
      default: true
    },

    deletable: {
      type: Boolean,
      default: false
    },

    isEdit: {
      type: Boolean,
      default: false
    },

    showButtonText: {
      type: Boolean,
      default: true
    },

    adding: {
      type: Boolean,
      default: false
    },

    round: {
      type: Boolean,
      default: true
    },

    addButtonType: {
      type: String,
      default: 'primary'
    },

    editButtonType: {
      type: String,
      default: 'info'
    },

    deleteButtonType: {
      type: String,
      default: 'danger'
    },

    showEditButton: {
      type: Boolean,
      default: true
    },

    showBackground: {
      type: Boolean,
      default: true
    },

    parentManaged: {
      type: Boolean,
      default: false
    },

    // XOL-371: when true this pane is the user's OWN self-service record
    // (e.g. /profile). View-only status does not restrict editing here — a
    // viewer=true user gets the same self-edit access as a regular user on
    // this surface. Default false preserves the view-only lock everywhere else.
    selfEditable: {
      type: Boolean,
      default: false
    },

    // XOL-552: lets the parent hold Save (and Add) shut without hiding the
    // button — e.g. the Backhaul Customer pane until a line is selected.
    // `saveDisabledTitle` is the tooltip that says why. Default false keeps
    // every other consumer unchanged.
    saveDisabled: {
      type: Boolean,
      default: false
    },

    saveDisabledTitle: {
      type: String,
      default: null
    }

  },

  computed: {
    isEditMode () {
      return this.parentManaged ? this.isEdit : this.editModeState
    },

    disable () {
      var disabled = (!this.editable && !this.isEditMode) || !this.isAdding()
      return this.isViewOnly() || disabled
    }
  },

  methods: {
    setEditMode (val) {
      if (!this.parentManaged) {
        this.editModeState = val
      }
    },

    add () {
      // XOL-552: a blocked add never leaves the pane or emits.
      if (this.saveDisabled) return

      this.setEditMode(this.adding ? this.adding : !this.editModeState)
      this.$emit('add')
    },

    edit () {
      this.setEditMode(!this.editModeState)
      this.$emit('edit')
    },

    deleted () {
      this.setEditMode(false)
      this.$emit('delete')
    },

    save () {
      // XOL-552: a blocked save must not leave edit mode (that would drop the
      // Save button and strand the user) or emit.
      if (this.saveDisabled) return

      this.setEditMode(false)
      this.$emit('save')
    },

    cancel () {
      this.setEditMode(false)
      this.$emit('cancel')
    },

    isEditable () {
      return this.isViewOnly() ? false : this.editable
    },

    isAdding () {
      return this.isViewOnly() ? false : this.adding
    },

    // --- form-field dirty tracking (XOL-231) ---------------------------------
    // EditPane wraps an arbitrary form in its <slot> and doesn't own the model,
    // so it detects edits by diffing the native control values under its root
    // against a baseline captured when edit mode begins. This catches changes to
    // a pane that OPENS in edit mode (no Edit click to flag it) and to Element UI
    // controls that update their inner value without firing a bubbling DOM event
    // (el-select, el-switch, el-date-picker). Evaluated lazily at close time.
    snapshotForm () {
      if (!this.$el || typeof this.$el.querySelectorAll !== 'function') return ''
      var nodes = this.$el.querySelectorAll('input, textarea, select')
      var parts = []
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i]
        parts.push((n.type === 'checkbox' || n.type === 'radio') ? (n.checked ? '1' : '0') : String(n.value))
      }
      return parts.join('␟')
    },

    isFormDirty () {
      if (this._formSnapshot === null || this._formSnapshot === undefined) return false
      return this.snapshotForm() !== this._formSnapshot
    },

    // Overrides DrawerDirtyMixin: dirty if the user entered edit mode (Edit/Add
    // click → drawerDirty) OR has changed any field since they started editing.
    isDrawerContentDirty () {
      return this.drawerDirty || this.isFormDirty()
    },

    onFormInteract (e) {
      // Capture the pre-edit baseline the FIRST time the user interacts with a
      // field while in edit mode. We listen for pointerdown / keydown / focusin
      // because not every control fires the same events: a text field fires
      // focusin, but an el-switch toggle (the No/Yes pills) fires only a
      // pointerdown+click on its <span> — no focus, no native change — so
      // focusin alone missed switch-only edits. pointerdown/keydown fire BEFORE
      // the value changes, so the baseline is the pre-edit state.
      //
      // Deliberately lazy (not on mount): the slot's form often populates
      // asynchronously (e.g. RequestWorkflow fetches its step data), so
      // snapshotting at mount would baseline a half-loaded form and read the
      // later-arriving values as a phantom change. A real user interaction
      // happens after the data has settled. Only trusted events count, so
      // programmatic focus()/auto-focus can't seed a bogus baseline. (XOL-231)
      if (this.isEditMode && e && e.isTrusted &&
          (this._formSnapshot === null || this._formSnapshot === undefined)) {
        this._formSnapshot = this.snapshotForm()
      }
    }

  },



  watch: {
    // Keep drawerDirty in sync with the edit-mode state. isEditMode resolves
    // both managed (parentManaged=false) and parentManaged modes. On Save,
    // Cancel, or Delete the parent calls setEditMode(false) which clears this.
    //
    // NOT immediate: drawerDirty must reflect a *user* entering edit mode, not a
    // pane that simply MOUNTS in edit mode. Some panes open ready-to-edit (e.g.
    // the Backhaul Customer pane in an auto-saving request workflow opens with
    // isEdit=true) — firing on the initial value flagged the drawer dirty before
    // any interaction, so closing popped a spurious "discard changes?" prompt
    // (XOL-231; the open-time false positive XOL-243 warned about). Only a
    // post-mount transition into edit mode (Edit/Add click) marks it dirty.
    isEditMode: function (v) {
      // Reset the form baseline on any edit-mode change; it is (re)captured on
      // the user's next interaction with a field via onFormInteract.
      this._formSnapshot = null
      this.drawerDirty = !!v
    }
  },

  data () {
    return {
      showTitleLocal: this.showTitle,
      editModeState: (this.isViewOnly() && !this.selfEditable) ? false : (this.adding ? true : this.isEdit)
    }
  },

  mounted () {
    if (this.title === null || this.title === '') {
      this.showTitleLocal = false
    }
//    console.log(`editPane [ editMode ${this.editMode} | editable ${this.editable} | disabled ${this.disable} | adding ${this.adding} ]`)
  }

}

</script>

<style scoped>

 .edit-pane {
   background-color: #F2F6FC;
   border-radius: 8.52px;
   padding-top: 1em;
   padding-left: 2em;
   padding-right: 2em;
   margin-bottom: 1em;
 }
</style>
